import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Light stream particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setCanvasSize();

    // Light stream lines
    const streams: Array<{
      x: number; y: number; length: number; speed: number;
      opacity: number; width: number; angle: number; color: string;
    }> = [];

    const streamColors = [
      'rgba(251,191,36,',
      'rgba(234,179,8,',
      'rgba(255,220,100,',
      'rgba(34,211,238,',
      'rgba(255,255,255,',
    ];

    for (let i = 0; i < 60; i++) {
      streams.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        length: Math.random() * 120 + 40,
        speed: Math.random() * 2.5 + 0.8,
        opacity: Math.random() * 0.18 + 0.04,
        width: Math.random() * 1.2 + 0.3,
        angle: -15 + Math.random() * 10,
        color: streamColors[Math.floor(Math.random() * streamColors.length)],
      });
    }

    // Floating glowing orbs
    const orbs: Array<{
      x: number; y: number; vx: number; vy: number;
      radius: number; opacity: number; color: string; pulse: number;
    }> = [];

    const orbColors = ['rgba(251,191,36,', 'rgba(34,211,238,', 'rgba(255,255,255,'];
    for (let i = 0; i < 18; i++) {
      orbs.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: Math.random() * 3 + 1,
        opacity: Math.random() * 0.5 + 0.15,
        color: orbColors[Math.floor(Math.random() * orbColors.length)],
        pulse: Math.random() * Math.PI * 2,
      });
    }

    // Horizontal scan lines (film reel effect)
    const scanLines: Array<{
      y: number; speed: number; opacity: number; width: number;
    }> = [];
    for (let i = 0; i < 5; i++) {
      scanLines.push({
        y: Math.random() * canvas.height,
        speed: Math.random() * 0.6 + 0.2,
        opacity: Math.random() * 0.06 + 0.02,
        width: Math.random() * 2 + 0.5,
      });
    }

    let frame = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;

      const angleRad = (-15 * Math.PI) / 180;
      const cosA = Math.cos(angleRad);
      const sinA = Math.sin(angleRad);

      // Draw light streams
      streams.forEach((s) => {
        s.y += s.speed;
        s.x += s.speed * Math.tan(angleRad) * 0.3;
        if (s.y > canvas.height + s.length) {
          s.y = -s.length;
          s.x = Math.random() * canvas.width;
        }

        const grad = ctx.createLinearGradient(
          s.x, s.y,
          s.x + s.length * sinA, s.y + s.length * cosA
        );
        grad.addColorStop(0, `${s.color}0)`);
        grad.addColorStop(0.4, `${s.color}${s.opacity})`);
        grad.addColorStop(1, `${s.color}0)`);

        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x + s.length * sinA, s.y + s.length * cosA);
        ctx.strokeStyle = grad;
        ctx.lineWidth = s.width;
        ctx.stroke();
      });

      // Draw scan lines
      scanLines.forEach((sl) => {
        sl.y += sl.speed;
        if (sl.y > canvas.height) sl.y = 0;
        ctx.beginPath();
        ctx.moveTo(0, sl.y);
        ctx.lineTo(canvas.width, sl.y);
        ctx.strokeStyle = `rgba(251,191,36,${sl.opacity})`;
        ctx.lineWidth = sl.width;
        ctx.stroke();
      });

      // Draw orbs
      orbs.forEach((orb) => {
        orb.x += orb.vx;
        orb.y += orb.vy;
        orb.pulse += 0.03;
        if (orb.x < 0 || orb.x > canvas.width) orb.vx *= -1;
        if (orb.y < 0 || orb.y > canvas.height) orb.vy *= -1;

        const pulseScale = 1 + Math.sin(orb.pulse) * 0.3;
        const r = orb.radius * pulseScale;

        const grad = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, r * 4);
        grad.addColorStop(0, `${orb.color}${orb.opacity})`);
        grad.addColorStop(0.4, `${orb.color}${orb.opacity * 0.4})`);
        grad.addColorStop(1, `${orb.color}0)`);

        ctx.beginPath();
        ctx.arc(orb.x, orb.y, r * 4, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => setCanvasSize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      {/* Background image — AI Film Academy style */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/483e6eda5697c1c0448d557775d4030c.jpg"
          alt="四相AI影視學院主視覺"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Cinematic overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-black/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/95" />
      <div className="absolute bottom-0 left-0 right-0 h-2/5 bg-gradient-to-t from-black to-transparent" />
      {/* Amber warm tint on right */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-900/20 via-transparent to-transparent" />

      {/* Light stream + orb canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" style={{ opacity: 0.9 }} />

      {/* Decorative vertical line */}
      <div className="absolute left-16 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-amber-400/30 to-transparent hidden md:block" />

      {/* Main content */}
      <div
        className={`relative z-10 h-full flex flex-col justify-center px-12 md:px-24 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        {/* Top badge */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-8 h-px bg-amber-400" />
          <span
            className="text-amber-400/90 text-xs tracking-[0.45em] font-light uppercase"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            四相AI影視學院 · AI Cinema Academy
          </span>
        </div>

        {/* Main headline */}
        <h1 className="leading-tight mb-6 max-w-3xl">
          <div
            className="text-5xl md:text-7xl font-black mb-1"
            style={{
              fontFamily: "'Noto Serif TC', serif",
              background: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 40%, #ffffff 70%, #fbbf24 100%)',
              backgroundSize: '200% 200%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 0 30px rgba(251,191,36,0.5))',
              animation: 'heroGlow 4s ease-in-out infinite',
            }}
          >
            用AI重新定義
          </div>
          <div
            className="text-5xl md:text-7xl font-black"
            style={{
              fontFamily: "'Noto Serif TC', serif",
              background: 'linear-gradient(135deg, #ffffff 0%, #fde68a 40%, #fbbf24 70%, #e0f7ff 100%)',
              backgroundSize: '200% 200%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 0 30px rgba(251,191,36,0.4))',
              animation: 'heroGlow 4s ease-in-out infinite reverse',
            }}
          >
            影視創作的邊界
          </div>
        </h1>

        {/* Subtitle */}
        <p
          className="text-white/80 text-base md:text-lg tracking-[0.2em] mb-8 font-light max-w-xl"
          style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
        >
          AI 影視製作 · 數位特效 · 虛擬人 IP · 商業短視頻
        </p>

        {/* Manifesto text */}
        <div className="mb-10 max-w-2xl">
          <p
            className="text-amber-300/90 text-sm md:text-base font-medium leading-relaxed mb-4"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            當別人還困在冗長的傳統溝通泥淖中，我們帶你掌握 AI 的視覺邏輯。
          </p>
          <p
            className="text-white/75 text-sm leading-[2] mb-5"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            在這裡，你將學會與 AI 深度協作的極效流程。從腳本發想、精準構圖到影視生成，不需百人團隊，你一個人就能產出兼具
            <span className="text-amber-300/90 font-medium">「高資訊密度」</span>
            與
            <span className="text-amber-300/90 font-medium">「商業美感」</span>
            的影視廣告動畫。
          </p>
          <div className="flex items-center gap-3">
            <div className="w-5 h-px bg-amber-400/50" />
            <p
              className="text-white/90 text-sm tracking-widest font-semibold"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              企劃・美術・後製，真正實現一人成軍
            </p>
          </div>
        </div>

        {/* CTA buttons */}
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={() => scrollToSection('courses')}
            className="px-8 py-3.5 bg-gradient-to-r from-amber-500 to-yellow-400 text-black font-bold text-sm tracking-widest rounded-sm hover:shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            開始學習
          </button>
          <button
            onClick={() => scrollToSection('portfolio')}
            className="px-8 py-3.5 border border-white/25 text-white/85 font-light text-sm tracking-widest rounded-sm hover:border-amber-400/60 hover:text-amber-400 transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            探索作品集
          </button>
          <button
            onClick={() => scrollToSection('contact')}
            className="px-8 py-3.5 border border-white/15 text-white/55 font-light text-sm tracking-widest rounded-sm hover:border-white/30 hover:text-white/80 transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            合作洽詢
          </button>
        </div>
      </div>

      {/* Bottom scroll hint */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
        <span
          className="text-white/50 text-xs tracking-[0.4em]"
          style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
        >
          SCROLL
        </span>
        <div className="w-px h-10 bg-gradient-to-b from-white/30 to-transparent animate-pulse" />
      </div>

      {/* Watermark */}
      <div className="absolute bottom-16 right-0 left-0 flex justify-center pointer-events-none overflow-hidden">
        <span
          className="text-[100px] md:text-[140px] font-black text-white/[0.025] tracking-widest select-none whitespace-nowrap"
          style={{ fontFamily: "'Bebas Neue', cursive" }}
        >
          LIUGUANG ACADEMY
        </span>
      </div>

      <style>{`
        @keyframes heroGlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </section>
  );
}
